import { Platform } from 'react-native';
import SimpleStorage from '../utils/simpleStorage';
import { ProductTemplate } from '../utils/productTemplates';
import { ServiceTemplate } from '../utils/serviceTemplates';

export interface CustomProductTemplate extends Omit<ProductTemplate, 'businessType' | 'categoryId'> {
  id: string;
  name: string; // Custom template name
  createdAt: string;
  updatedAt: string;
  businessOwnerId?: string; // Optional: associate with business owner
}

export interface CustomServiceTemplate extends Omit<ServiceTemplate, 'businessType' | 'categoryId'> {
  id: string;
  name: string; // Custom template name
  createdAt: string;
  updatedAt: string;
  businessOwnerId?: string; // Optional: associate with business owner
}

class TemplateService {
  private readonly PRODUCT_TEMPLATES_KEY = 'custom_product_templates';
  private readonly SERVICE_TEMPLATES_KEY = 'custom_service_templates';

  // Product Templates
  async getCustomProductTemplates(businessOwnerId?: string): Promise<CustomProductTemplate[]> {
    try {
      const templates = await SimpleStorage.getItem(this.PRODUCT_TEMPLATES_KEY);
      const allTemplates: CustomProductTemplate[] = templates ? JSON.parse(templates) : [];
      
      if (businessOwnerId) {
        return allTemplates.filter(t => !t.businessOwnerId || t.businessOwnerId === businessOwnerId);
      }
      return allTemplates;
    } catch (error) {
      console.error('Error getting custom product templates:', error);
      return [];
    }
  }

  async saveCustomProductTemplate(template: Omit<CustomProductTemplate, 'id' | 'createdAt' | 'updatedAt'>): Promise<CustomProductTemplate> {
    try {
      const templates = await this.getCustomProductTemplates();
      const newTemplate: CustomProductTemplate = {
        ...template,
        id: `custom_product_template_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      
      templates.push(newTemplate);
      await SimpleStorage.setItem(this.PRODUCT_TEMPLATES_KEY, JSON.stringify(templates));
      
      // Also save to Firestore if on web
      if (Platform.OS === 'web' && template.businessOwnerId) {
        try {
          const { doc, setDoc } = await import('firebase/firestore');
          const { getWebDb } = await import('./unifiedAuthService');
          const firestoreDb = getWebDb();
          if (firestoreDb) {
            const templateDocRef = doc(firestoreDb, 'businesses', template.businessOwnerId, 'product_templates', newTemplate.id);
            await setDoc(templateDocRef, newTemplate);
            console.log(`✅ TemplateService: Custom product template saved to Firestore: ${newTemplate.id}`);
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  TemplateService: Could not save product template to Firestore:', firestoreError);
        }
      }
      
      console.log(`✅ TemplateService: Custom product template saved: ${newTemplate.name}`);
      return newTemplate;
    } catch (error) {
      console.error('Error saving custom product template:', error);
      throw error;
    }
  }

  async deleteCustomProductTemplate(templateId: string, businessOwnerId?: string): Promise<boolean> {
    try {
      const templates = await this.getCustomProductTemplates();
      const updatedTemplates = templates.filter(t => t.id !== templateId);
      await SimpleStorage.setItem(this.PRODUCT_TEMPLATES_KEY, JSON.stringify(updatedTemplates));
      
      // Also delete from Firestore if on web
      if (Platform.OS === 'web' && businessOwnerId) {
        try {
          const { doc, deleteDoc } = await import('firebase/firestore');
          const { getWebDb } = await import('./unifiedAuthService');
          const firestoreDb = getWebDb();
          if (firestoreDb) {
            const templateDocRef = doc(firestoreDb, 'businesses', businessOwnerId, 'product_templates', templateId);
            await deleteDoc(templateDocRef);
            console.log(`✅ TemplateService: Custom product template deleted from Firestore: ${templateId}`);
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  TemplateService: Could not delete product template from Firestore:', firestoreError);
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting custom product template:', error);
      return false;
    }
  }

  async exportProductTemplateAsJSON(template: CustomProductTemplate | ProductTemplate): Promise<string> {
    return JSON.stringify(template, null, 2);
  }

  // Service Templates
  async getCustomServiceTemplates(businessOwnerId?: string): Promise<CustomServiceTemplate[]> {
    try {
      const templates = await SimpleStorage.getItem(this.SERVICE_TEMPLATES_KEY);
      const allTemplates: CustomServiceTemplate[] = templates ? JSON.parse(templates) : [];
      
      if (businessOwnerId) {
        return allTemplates.filter(t => !t.businessOwnerId || t.businessOwnerId === businessOwnerId);
      }
      return allTemplates;
    } catch (error) {
      console.error('Error getting custom service templates:', error);
      return [];
    }
  }

  async saveCustomServiceTemplate(template: Omit<CustomServiceTemplate, 'id' | 'createdAt' | 'updatedAt'>): Promise<CustomServiceTemplate> {
    try {
      const templates = await this.getCustomServiceTemplates();
      const newTemplate: CustomServiceTemplate = {
        ...template,
        id: `custom_service_template_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      
      templates.push(newTemplate);
      await SimpleStorage.setItem(this.SERVICE_TEMPLATES_KEY, JSON.stringify(templates));
      
      // Also save to Firestore if on web
      if (Platform.OS === 'web' && template.businessOwnerId) {
        try {
          const { doc, setDoc } = await import('firebase/firestore');
          const { getWebDb } = await import('./unifiedAuthService');
          const firestoreDb = getWebDb();
          if (firestoreDb) {
            const templateDocRef = doc(firestoreDb, 'businesses', template.businessOwnerId, 'service_templates', newTemplate.id);
            await setDoc(templateDocRef, newTemplate);
            console.log(`✅ TemplateService: Custom service template saved to Firestore: ${newTemplate.id}`);
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  TemplateService: Could not save service template to Firestore:', firestoreError);
        }
      }
      
      console.log(`✅ TemplateService: Custom service template saved: ${newTemplate.name}`);
      return newTemplate;
    } catch (error) {
      console.error('Error saving custom service template:', error);
      throw error;
    }
  }

  async deleteCustomServiceTemplate(templateId: string, businessOwnerId?: string): Promise<boolean> {
    try {
      const templates = await this.getCustomServiceTemplates();
      const updatedTemplates = templates.filter(t => t.id !== templateId);
      await SimpleStorage.setItem(this.SERVICE_TEMPLATES_KEY, JSON.stringify(updatedTemplates));
      
      // Also delete from Firestore if on web
      if (Platform.OS === 'web' && businessOwnerId) {
        try {
          const { doc, deleteDoc } = await import('firebase/firestore');
          const { getWebDb } = await import('./unifiedAuthService');
          const firestoreDb = getWebDb();
          if (firestoreDb) {
            const templateDocRef = doc(firestoreDb, 'businesses', businessOwnerId, 'service_templates', templateId);
            await deleteDoc(templateDocRef);
            console.log(`✅ TemplateService: Custom service template deleted from Firestore: ${templateId}`);
          }
        } catch (firestoreError: any) {
          console.warn('⚠️  TemplateService: Could not delete service template from Firestore:', firestoreError);
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting custom service template:', error);
      return false;
    }
  }

  async exportServiceTemplateAsJSON(template: CustomServiceTemplate | ServiceTemplate): Promise<string> {
    return JSON.stringify(template, null, 2);
  }
}

export const templateService = new TemplateService();
export default templateService;
